/*
 * © 2025 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
import { ProcessUpdatePresentation } from "@/presentation/processUpdates";
import {
  MachineType,
  OffsetAdjustmentAppliedAndCalculation,
  ProcessUpdatesTableType,
  ProcessAction,
} from "@centralwebteam/narwhal";
import { groupBy, orderBy, uniqBy, uniqWith } from "lodash/fp";
import { ProcessUpdatePerf } from "@/store/state";

const tempProcessUpdate = (name = "") => {
  return {
    name: name,
    targetMachine: {
      name: name,
      type: undefined,
    },
    offsets: {
      exceeded: 0,
      applied: 0,
    },
    runMode: undefined,
    characteristics: {
      fails: 0,
      passes: 0,
    },
    jobName: "",
    measurementVerdict: "",
    sourceMachine: {
      name: "",
      type: undefined,
    },
    time: undefined,
    controlledOffset: "",
    displayName: "",
    totalUpdates: 0,
  };
};

export function getTableKey(
  item: ProcessUpdatePresentation | ProcessUpdatePerf,
  type: ProcessUpdatesTableType
) {
  switch (type) {
    case "Characteristic":
      return `${item.targetMachine?.name},${item.jobName},${item.sourceMachine!.name}`;
    case "Job":
      return `${item.targetMachine?.name},${item.jobName}`;
    case "Machine":
      return `${item.targetMachine?.name}`;
  }
}

export function getDetailTableKey(
  item: ProcessUpdatePresentation | ProcessUpdatePerf,
  type: ProcessUpdatesTableType
) {
  switch (type) {
    case "Characteristic":
      return `${item.targetMachine?.name},${item.jobName},${item.sourceMachine!.name},${item.controlledOffset}`;
    case "Job":
      return `${item.targetMachine?.name},${item.jobName}`;
    case "Machine":
      return `${item.targetMachine?.name},${item.controlledOffset}`;
  }
}

function getGroupingKey(
  update: ProcessUpdatePresentation,
  type: ProcessUpdatesTableType
): (string | undefined)[] {
  switch (type) {
    case "Characteristic":
      return [
        update.targetMachine?.name,
        update.jobName,
        update.sourceMachine?.name,
      ];
    case "Job":
      return [update.targetMachine?.name, update.jobName];
    case "Machine":
      return [update.targetMachine?.name];
  }
}

function getUniqueIdentifier(
  type: ProcessUpdatesTableType
): (pu: ProcessUpdatePresentation) => any[] {
  switch (type) {
    case "Characteristic":
      return (pu) => [
        pu.jobName,
        pu.measuredFeature,
        pu.measuredCharacteristic,
        pu.time,
      ];
    case "Job":
      return (pu) => [pu.jobName, pu.time];
    case "Machine":
      return (pu) => [pu.time];
  }
}

function getUniqWithComparator(
  type: ProcessUpdatesTableType
): (a: ProcessUpdatePresentation, b: ProcessUpdatePresentation) => boolean {
  switch (type) {
    case "Characteristic":
      return (a, b) =>
        a.targetMachine!.id === b.targetMachine!.id &&
        a.sourceMachine!.id === b.sourceMachine!.id &&
        a.controlledOffset === b.controlledOffset &&
        a.jobName === b.jobName;
    case "Job":
      return (a, b) =>
        a.targetMachine!.id === b.targetMachine!.id &&
        a.jobName === b.jobName &&
        a.controlledOffset === b.controlledOffset &&
        a.correlationId === b.correlationId;

    case "Machine":
      return (a, b) =>
        a.targetMachine!.id === b.targetMachine!.id &&
        a.controlledOffset === b.controlledOffset &&
        a.correlationId === b.correlationId;
  }
}

export function compileTableUpdates(
  processUpdates: ProcessUpdatePresentation[],
  type: ProcessUpdatesTableType
) {
  const orderedToolOffsetAppliedEvents = orderBy(
    "time",
    "desc",
    processUpdates
  ).filter((processUpdate) => processUpdate.grouping === type);

  const uniqueToolOffsetAppliedEvents = uniqBy(getUniqueIdentifier(type))(
    orderedToolOffsetAppliedEvents
  );

  const groupedToolOffsetAppliedEventsByKey = groupBy(
    (pu) => getGroupingKey(pu, type),
    uniqueToolOffsetAppliedEvents
  );

  const tempProcessUpdates: Map<string, ProcessUpdatePerf> = new Map();
  const tempProcessUpdatesDetails: Map<string, ProcessUpdatePresentation[]> =
    new Map();
  let processUpdateDetailsMore: Dictionary<
    Dictionary<ProcessUpdatePresentation[]>
  > = {};

  Object.keys(groupedToolOffsetAppliedEventsByKey).forEach((key) => {
    const groupedToolOffsets = groupedToolOffsetAppliedEventsByKey[key];
    const uniqueGroupedToolOffsets = uniqWith(
      getUniqWithComparator(type),
      groupedToolOffsets
    );

    for (let i = uniqueGroupedToolOffsets.length - 1; i >= 0; i--) {
      const event = uniqueGroupedToolOffsets[i];
      const tableKey = getTableKey(event, type);

      if (!tempProcessUpdates.has(tableKey)) {
        tempProcessUpdates.set(tableKey, tempProcessUpdate(tableKey));
      }

      const processUpdatesPerf = tempProcessUpdates.get(tableKey)!;
      processUpdatesPerf.name = tableKey;
      processUpdatesPerf.targetMachine.name = event.targetMachine!.name;
      processUpdatesPerf.targetMachine.type =
        (event.targetMachine!.type as MachineType) ?? undefined;
      event.lastOffsetAdjustment === "Exceeded"
        ? processUpdatesPerf.offsets.exceeded++
        : processUpdatesPerf.offsets.applied++;
      processUpdatesPerf.runMode = event.runMode;
      event.measurementVerdict === "Pass"
        ? processUpdatesPerf.characteristics.passes++
        : processUpdatesPerf.characteristics.fails++;
      processUpdatesPerf.jobName = event.jobName || "";
      processUpdatesPerf.measurementVerdict = event.measurementVerdict;
      processUpdatesPerf.sourceMachine = event.sourceMachine!;
      processUpdatesPerf.time = event.time;
      processUpdatesPerf.controlledOffset = event.controlledOffset;
      processUpdatesPerf.displayName = event.displayName!;

      if (type !== "Characteristic") {
        processUpdatesPerf.totalUpdates = uniqueGroupedToolOffsets.length;
      }

      if (!tempProcessUpdatesDetails.has(tableKey)) {
        tempProcessUpdatesDetails.set(
          tableKey,
          uniqueGroupedToolOffsets.filter(
            (o) => tableKey === getTableKey(o, type)
          )
        );
      }

      if (type === "Characteristic") {
        processUpdateDetailsMore = uniqueToolOffsetAppliedEvents.reduce(
          (accumulator, update) => {
            const outerKey = getTableKey(update, type);
            const innerKey = getDetailTableKey(update, type);

            return {
              ...accumulator,
              [outerKey]: {
                ...(accumulator[outerKey] ?? {}),
                [innerKey]: uniqBy("correlationId", [
                  ...(accumulator[outerKey]?.[innerKey] ?? []),
                  update,
                ]),
              },
            };
          },
          {} as Dictionary<Dictionary<ProcessUpdatePresentation[]>>
        );
      }
    }
  });

  const processUpdatesPerf = {} as MappyAbsolute<ProcessUpdatePerf>;
  tempProcessUpdates.forEach((j, name) => {
    processUpdatesPerf[name] = j;
  });

  const processUpdateDetails = {} as MappyAbsolute<ProcessUpdatePresentation[]>;
  tempProcessUpdatesDetails.forEach((j, name) => {
    processUpdateDetails[name] = j;
  });

  if (type === "Characteristic") {
    Object.values(processUpdatesPerf).forEach((item) => {
      const keyForDetailTable = getTableKey(item, type);
      const dataForCount = processUpdateDetailsMore[keyForDetailTable];

      if (dataForCount) {
        item.totalUpdates = Object.values(dataForCount).reduce(
          (total, updates) => total + updates.length,
          0
        );
      } else {
        item.totalUpdates = 0;
      }
    });
  }

  return {
    processUpdatesPerf,
    processUpdateDetails,
    processUpdateDetailsMore,
  };
}

export function computeAllGroupedTableData(processActions: ProcessAction[]) {
  const processUpdatesPresentations = processActions.map(
    (item) =>
      new ProcessUpdatePresentation(
        item as OffsetAdjustmentAppliedAndCalculation
      )
  );

  const characteristicData = compileTableUpdates(
    processUpdatesPresentations.filter(
      (item) => item.grouping === "Characteristic"
    ),
    "Characteristic"
  );

  const jobData = compileTableUpdates(
    processUpdatesPresentations.filter((item) => item.grouping === "Job"),
    "Job"
  );

  const machineData = compileTableUpdates(
    processUpdatesPresentations.filter((item) => item.grouping === "Machine"),
    "Machine"
  );

  return {
    characteristic: characteristicData,
    job: jobData,
    machine: machineData,
  };
}
